import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TextField,
  CircularProgress,
  Avatar,
  Autocomplete,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Snackbar,
  Alert,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip as ReTooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
} from 'recharts';
import { collection, query, where, orderBy, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../firebase-config';
import { Delete } from '@mui/icons-material';
import { useAuth } from '../auth-context';

const formatDate = (date) => date.toISOString().split('T')[0];
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#A569BD', '#CD6155', '#5DADE2'];

const WasteManagementTab = () => {
  const { userRole } = useAuth();
  const [loading, setLoading] = useState(true);
  const [wasteItems, setWasteItems] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [itemsList, setItemsList] = useState([]);
  const [filterRestaurant, setFilterRestaurant] = useState('');
  const [filterItem, setFilterItem] = useState(null);
  const [filterStartDate, setFilterStartDate] = useState(() => {
    let d = new Date();
    d.setDate(d.getDate() - 30);
    return formatDate(d);
  });
  const [filterEndDate, setFilterEndDate] = useState(() => formatDate(new Date()));
  
  // Delete functionality states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Fetch waste data from Firestore filtered by date
  useEffect(() => {
    setLoading(true);
    const qBase = collection(db, 'wastage');
    const q = query(qBase, where('date', '>=', filterStartDate), where('date', '<=', filterEndDate), orderBy('date', 'desc'));
    getDocs(q)
      .then(snapshot => {
        let items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setWasteItems(items);

        // Extracting unique items for items filter (searchable dropdown)
        const uniqueItemsSet = new Set();
        items.forEach(i => {
          if (i.itemName && typeof i.itemName === "string") uniqueItemsSet.add(i.itemName.trim());
        });
        setItemsList(Array.from(uniqueItemsSet).sort());

        setLoading(false);
      })
      .catch(error => {
        console.error('Error fetching waste data:', error);
        setLoading(false);
      });
  }, [filterStartDate, filterEndDate]);

  // Fetch restaurants list from users collection (adjust collection if different)
  useEffect(() => {
    const fetchRestaurants = async () => {
      try {
        const qUsers = collection(db, 'users');
        const snapshot = await getDocs(qUsers);
        const rests = snapshot.docs
          .map(doc => ({
            id: doc.id,
            name: doc.data().restaurantName || doc.data().name || doc.id,
          }));
        setRestaurants(rests);
      } catch (e) {
        console.error('Error fetching restaurants:', e);
      }
    };
    fetchRestaurants();
  }, []);

  // Multi-filtering data by restaurant and item
  const filteredWasteItems = useMemo(() => {
    let filtered = [...wasteItems];
    if (filterRestaurant) filtered = filtered.filter(item => item.userId === filterRestaurant);
    if (filterItem) filtered = filtered.filter(item => (item.itemName && item.itemName.trim() === filterItem));
    return filtered;
  }, [wasteItems, filterRestaurant, filterItem]);

  // Units-aware aggregation: { unit: totalQuantity }
  const wasteQuantityByUnit = useMemo(() => {
    const unitMap = {};
    filteredWasteItems.forEach(item => {
      const qty = parseFloat(item.wastedQuantity) || 0;
      let unit = item.unit || 'pcs';
      unit = typeof unit === 'string' ? unit.trim() : String(unit);
      if (!unitMap[unit]) unitMap[unit] = 0;
      unitMap[unit] += qty;
    });
    return unitMap;
  }, [filteredWasteItems]);

  // Show sum for each unit as "12 kg + 8 pcs"
  const displayWasteQuantity = Object.entries(wasteQuantityByUnit)
    .map(([unit, qty]) => `${qty.toFixed(2)} ${unit}`)
    .join(' + ');

  const totalWasteValue = useMemo(
    () => filteredWasteItems.reduce((sum, item) => sum + (parseFloat(item.estimatedValue) || 0), 0),
    [filteredWasteItems]
  );
  const totalWasteEvents = useMemo(() => filteredWasteItems.length, [filteredWasteItems]);

  // Charts – these should ALSO respect units, so for charts just use sum of all quantities (but label clearly)
  // Or, you can show value-based charts for cross-unit
  const wasteByDate = useMemo(() => {
    const map = {};
    filteredWasteItems.forEach(({ date, wastedQuantity, estimatedValue, unit }) => {
      if (!date) return;
      if (!map[date]) map[date] = { date, byUnit: {}, estimatedValue: 0 };
      const u = (unit || 'pcs').trim();
      map[date].byUnit[u] = (map[date].byUnit[u] || 0) + (parseFloat(wastedQuantity) || 0);
      map[date].estimatedValue += parseFloat(estimatedValue) || 0;
    });
    // Just sum all units for the "wastedQuantity" for chart display; or optionally split by units if needed
    return Object.values(map)
      .map(({ date, byUnit, estimatedValue }) => ({
        date,
        wastedQuantity: Object.values(byUnit).reduce((a, b) => a + b, 0),
        estimatedValue,
      }))
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [filteredWasteItems]);

  const wasteByReason = useMemo(() => {
    const reasonMap = {};
    filteredWasteItems.forEach(({ reason, wastedQuantity, unit }) => {
      if (!reason) return;
      let u = (unit || 'pcs').trim();
      if (!reasonMap[reason]) reasonMap[reason] = {};
      reasonMap[reason][u] = (reasonMap[reason][u] || 0) + (parseFloat(wastedQuantity) || 0);
    });
    // For pie charting, flatten units by summing, or show reason+unit if units matter
    return Object.entries(reasonMap).map(([reason, unitObj]) => ({
      name: reason,
      value: Object.values(unitObj).reduce((a, b) => a + b, 0), // sum
    }));
  }, [filteredWasteItems]);

  const wasteByRestaurant = useMemo(() => {
    const restMap = {};
    filteredWasteItems.forEach(({ userId, wastedQuantity, unit }) => {
      if (!userId) return;
      let u = (unit || 'pcs').trim();
      if (!restMap[userId]) restMap[userId] = {};
      restMap[userId][u] = (restMap[userId][u] || 0) + (parseFloat(wastedQuantity) || 0);
    });
    // For bar chart, sum all units; or show legend for units if needed
    return Object.entries(restMap).map(([id, unitObj]) => {
      const restaurant = restaurants.find(r => r.id === id)?.name || id;
      return { name: restaurant, quantity: Object.values(unitObj).reduce((a, b) => a + b, 0) };
    });
  }, [filteredWasteItems, restaurants]);

  const wasteByItem = useMemo(() => {
    const itemMap = {};
    filteredWasteItems.forEach(({ itemName, wastedQuantity, unit }) => {
      if (!itemName) return;
      let u = (unit || 'pcs').trim();
      if (!itemMap[itemName]) itemMap[itemName] = {};
      itemMap[itemName][u] = (itemMap[itemName][u] || 0) + (parseFloat(wastedQuantity) || 0);
    });
    const itemsArr = Object.entries(itemMap).map(([name, unitObj]) => ({
      name,
      quantity: Object.values(unitObj).reduce((a, b) => a + b, 0),
    }));
    return itemsArr.sort((a, b) => b.quantity - a.quantity).slice(0, 10);
  }, [filteredWasteItems]);

  // Delete handler functions
  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    
    setDeleteLoading(true);
    try {
      // Delete from Firestore
      await deleteDoc(doc(db, 'wastage', itemToDelete.id));
      
      // Update local state to remove the item
      setWasteItems(prev => prev.filter(item => item.id !== itemToDelete.id));
      
      // Show success message
      setSnackbarMessage('Waste item deleted successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      
    } catch (error) {
      console.error('Error deleting waste item:', error);
      setSnackbarMessage('Error deleting waste item. Please try again.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setDeleteLoading(false);
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const columns = [
    { field: 'date', headerName: 'Date', width: 110 },
    {
      field: 'userId',
      headerName: 'Restaurant',
      width: 350,
      valueGetter: (params) => {
        const rest = restaurants.find(r => r.id === params.value);
        return rest?.name || params.value;
      },
    },
    { field: 'itemName', headerName: 'Item Name', width: 180 },
    { field: 'wastedQuantity', headerName: 'Quantity', width: 100, type: 'number' },
    { field: 'unit', headerName: 'Unit', width: 80 },
    { field: 'reason', headerName: 'Reason', width: 160 },
    { field: 'estimatedValue', headerName: 'Value (£)', width: 110, type: 'number' },
    {
      field: 'imageUrl',
      headerName: 'Image',
      width: 100,
      renderCell: (params) =>
        params.value ? (
          <a href={params.value} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
            <Avatar variant="square" src={params.value} alt="waste" sx={{ width: 60, height: 40 }} />
          </a>
        ) : (
          <Typography variant="body2" color="textSecondary">No Image</Typography>
        ),
      sortable: false,
      filterable: false,
    },
    { field: 'description', headerName: 'Description', width: 220 },
    // Delete action column - only show for admin/superadmin
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        ['admin', 'superadmin'].includes(userRole) ? (
          <IconButton
            color="error"
            onClick={() => handleDeleteClick(params.row)}
            size="small"
            disabled={deleteLoading}
          >
            <Delete />
          </IconButton>
        ) : null
      ),
    },
  ];

  return (
    <Box p={3} sx={{ backgroundColor: '#fafafa', minHeight: '100vh' }}>
      <Typography variant="h4" gutterBottom>
        Waste Management Dashboard
      </Typography>

      {/* Filters */}
      <Grid container spacing={2} alignItems="flex-end" mb={3}>
        <Grid item xs={12} sm={3} md={2.7}>
          <TextField
            label="Start Date"
            type="date"
            variant="outlined"
            fullWidth
            value={filterStartDate}
            onChange={(e) => setFilterStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} sm={3} md={2.7}>
          <TextField
            label="End Date"
            type="date"
            variant="outlined"
            fullWidth
            value={filterEndDate}
            onChange={(e) => setFilterEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} sm={3} md={3}>
          <FormControl fullWidth>
            <InputLabel id="restaurant-select-label" shrink>
              Restaurant
            </InputLabel>
            <Select
              labelId="restaurant-select-label"
              value={filterRestaurant}
              label="Restaurant"
              displayEmpty
              renderValue={(selected) =>
                selected
                  ? restaurants.find(r => r.id === selected)?.name
                  : "All Restaurants"
              }
              onChange={(e) => setFilterRestaurant(e.target.value)}
              inputProps={{ 'aria-label': 'Restaurant' }}
            >
              <MenuItem value="">
                <em>All Restaurants</em>
              </MenuItem>
              {restaurants.map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  {r.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={3} md={3}>
          <Autocomplete
            options={itemsList}
            value={filterItem}
            onChange={(_e, v) => setFilterItem(v)}
            renderInput={(params) => (
              <TextField {...params} label="Item (Search)" variant="outlined" />
            )}
            clearOnEscape
            isOptionEqualToValue={(option, value) => option === value}
            sx={{ minWidth: 140 }}
          />
        </Grid>
      </Grid>

      {/* Loading */}
      {loading ? (
        <Box textAlign="center" mt={10}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* KPIs */}
          <Grid container spacing={3} mb={4}>
            <Grid item xs={12} sm={4}>
              <Card sx={{ bgcolor: '#e3f2fd' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Total Waste Quantity
                  </Typography>
                  <Typography variant="h4" sx={{ whiteSpace: 'pre-line' }}>
                    {displayWasteQuantity || "0"}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card sx={{ bgcolor: '#ffebee' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Total Waste Value (£)
                  </Typography>
                  <Typography variant="h4">{totalWasteValue.toFixed(2)}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card sx={{ bgcolor: '#e8f5e9' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Waste Events
                  </Typography>
                  <Typography variant="h4">{totalWasteEvents}</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Charts */}
          <Grid container spacing={4} mb={6}>
            <Grid item xs={12} md={6} lg={6}>
              <Typography variant="h6" mb={2}>
                Waste Over Time (Quantity and Value)
              </Typography>
              <LineChart width={600} height={300} data={wasteByDate}>
                <XAxis dataKey="date" />
                <YAxis yAxisId="left" orientation="left" stroke="#8884d8" />
                <YAxis yAxisId="right" orientation="right" stroke="#82ca9d" />
                <ReTooltip />
                <Legend />
                <Line yAxisId="left" type="monotone" dataKey="wastedQuantity" name="Quantity (sum)" stroke="#8884d8" />
                <Line yAxisId="right" type="monotone" dataKey="estimatedValue" name="Value (£)" stroke="#82ca9d" />
              </LineChart>
            </Grid>

            <Grid item xs={12} md={6} lg={6}>
              <Typography variant="h6" mb={2}>
                Waste by Reason
              </Typography>
              {wasteByReason.length ? (
                <PieChart width={400} height={300}>
                  <Pie
                    data={wasteByReason}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label
                  >
                    {wasteByReason.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <ReTooltip />
                </PieChart>
              ) : (
                <Typography>No data</Typography>
              )}
            </Grid>

            <Grid item xs={12} md={6} lg={6}>
              <Typography variant="h6" mb={2}>
                Waste by Restaurant (Quantity)
              </Typography>
              {wasteByRestaurant.length ? (
                <BarChart width={600} height={300} data={wasteByRestaurant}>
                  <XAxis dataKey="name" />
                  <YAxis />
                  <ReTooltip />
                  <Bar dataKey="quantity" fill="#82ca9d" />
                </BarChart>
              ) : (
                <Typography>No data</Typography>
              )}
            </Grid>

            <Grid item xs={12} md={6} lg={6}>
              <Typography variant="h6" mb={2}>
                Top 10 Waste Items (Quantity)
              </Typography>
              {wasteByItem.length ? (
                <BarChart width={600} height={300} data={wasteByItem}>
                  <XAxis dataKey="name" />
                  <YAxis />
                  <ReTooltip />
                  <Bar dataKey="quantity" fill="#8884d8" />
                </BarChart>
              ) : (
                <Typography>No data</Typography>
              )}
            </Grid>
          </Grid>

          {/* Waste Items Table */}
          <Typography variant="h6" mb={2}>
            Waste Events Detail
          </Typography>
          <Box sx={{ height: 550, backgroundColor: 'white' }}>
            <DataGrid
              rows={filteredWasteItems}
              columns={columns}
              pageSize={10}
              rowsPerPageOptions={[10, 25, 50]}
              disableSelectionOnClick
              getRowId={(row) => row.id}
            />
          </Box>
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle id="delete-dialog-title">
          Confirm Deletion
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this waste entry?
            <br />
            <strong>Item:</strong> {itemToDelete?.itemName}
            <br />
            <strong>Quantity:</strong> {itemToDelete?.wastedQuantity} {itemToDelete?.unit}
            <br />
            <strong>Date:</strong> {itemToDelete?.date}
            <br />
            <br />
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            variant="contained"
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={16} /> : null}
          >
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WasteManagementTab;